import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import CommentsPanel from '../../comments/CommentsPanel';
import FileChatPanel from '../../chat/FileChatPanel';
import { chatCounts, chatKeys } from '../../chat/chatApi';

const TABS = [
  { key: 'comments', label: 'Comments' },
  { key: 'chat', label: 'Chat' },
];

/**
 * The file viewer's conversation sidebar (ported from the old app's
 * file-conversation-sidebar): Comments and Live-chat tabs. Tabs mount lazily
 * on first visit, then stay mounted but hidden — keeping the chat socket
 * subscription and scroll position alive across switches.
 */
export default function ConversationSidebar({ file }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('comments');
  const [visited, setVisited] = useState({ comments: true, chat: false });

  // Polled independently of the chat panel itself, so a message that arrives
  // while the Chat tab is closed (or never opened) still raises a bubble here.
  const countsKey = chatKeys.counts('file', [file.id]);
  const { data: counts = {} } = useQuery({
    queryKey: countsKey,
    queryFn: () => chatCounts('file', [file.id]),
    enabled: Boolean(file?.id),
    refetchInterval: 15_000,
  });
  const chatUnread = counts[file.id]?.unread ?? 0;

  const select = (key) => {
    setTab(key);
    setVisited((v) => ({ ...v, [key]: true }));
    if (key === 'chat') queryClient.invalidateQueries({ queryKey: countsKey });
  };

  return (
    <aside className="flex w-[360px] shrink-0 flex-col overflow-hidden border-l border-slate-200 bg-slate-100">
      <div className="flex shrink-0 border-b border-slate-200">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => select(key)}
            className={clsx(
              'flex-1 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
              tab === key
                ? 'border-brand-500 text-slate-800'
                : 'border-transparent text-slate-400 hover:text-slate-600',
            )}
          >
            {label}
            {key === 'chat' && tab !== 'chat' && chatUnread > 0 && (
              <span className="ml-1.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold text-white">
                {chatUnread > 9 ? '9+' : chatUnread}
              </span>
            )}
          </button>
        ))}
      </div>

      {visited.comments && (
        <div hidden={tab !== 'comments'} className="min-h-0 flex-1 p-3">
          <CommentsPanel targetType="file" targetId={file.id} variant="sidebar" />
        </div>
      )}
      {visited.chat && (
        <div hidden={tab !== 'chat'} className="min-h-0 flex-1">
          <FileChatPanel fileId={file.id} />
        </div>
      )}
    </aside>
  );
}
